import { type ReactNode, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePerformGlobalRefresh } from "../../../../components/header/hooks";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { EditDepartmentLocationConfigMode } from "../../../../utils/models";
import { LocationCellProvider } from "../../v2/Cells/LocationCellContext";
import { EditDepartmentLocationConfig } from "../../v2/EditDepartmentLocationConfig/EditDepartmentLocationConfig";
import type { LocationUpdateInfo } from "../../v2/EditDepartmentLocationConfig/useEditDepartmentLocationConfigState";

type Props = {
	/** Whether the create popover is open for this section. */
	open: boolean;
	/** Close request (false to close). */
	onClose: () => void;
	/** Sunday-first week-start day (0-6) for the per-day selectors. */
	weekStartDay: number;
	/** Section names already in use — feeds the section dropdown's create-new list. */
	uniqueSections: string[];
	/** Pre-select this section (the section band the "+" was clicked on). */
	initialSection?: string;
	/** Refetch the coverage grid after the new location is created. */
	onCoverageRefresh: () => Promise<unknown>;
	/** The section band that anchors the popover (mirrors v2's cell-anchored editor). */
	children: ReactNode;
};

/** Section "+" create: opens the shared v2 `EditDepartmentLocationConfig` (Create mode) as a band-anchored popover. */
export const CoverageLocationCreate = ({ open, onClose, weekStartDay, uniqueSections, initialSection, onCoverageRefresh, children }: Props) => {
	const { t } = useTranslation();
	const { isMedicalDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const [displayCloseWarning, setDisplayCloseWarning] = useState(false);
	const [isSomeDropdownOpen, setIsSomeDropdownOpen] = useState(false);
	const didEditAnythingRef = useRef(false);

	const close = useCallback(() => {
		setDisplayCloseWarning(false);
		didEditAnythingRef.current = false;
		onClose();
	}, [onClose]);

	const handleRequestClose = useCallback(() => {
		if (isSomeDropdownOpen) return;
		if (didEditAnythingRef.current && !displayCloseWarning) {
			setDisplayCloseWarning(true);
			return;
		}
		close();
	}, [isSomeDropdownOpen, displayCloseWarning, close]);

	const handleSave = useCallback(
		async (updateInfo?: LocationUpdateInfo) => {
			if (updateInfo?.refreshDepartmentData) {
				await performGlobalRefresh({ avoidRefreshFor: { nursesData: true, requestsData: true, notificationsData: true } });
			}
			await onCoverageRefresh();
			showSnackbar(t("schedule.template.v3.editor.created"), "success");
			close();
		},
		[performGlobalRefresh, onCoverageRefresh, showSnackbar, t, close],
	);

	return (
		<>
			<SystemReactTinyPopover
				isOpen={open}
				onClickOutside={handleRequestClose}
				positions={["bottom", "top"]}
				align="start"
				disabledChildrenWrapper
				data-testid="ms3-location-create"
				content={
					<LocationCellProvider weekStartDay={weekStartDay}>
						<EditDepartmentLocationConfig
							mode={EditDepartmentLocationConfigMode.Create}
							initialSection={initialSection}
							occupiedDays={[]}
							uniqueSections={uniqueSections}
							isSynergyMS={isMedicalDepartment}
							onDidChangeNotifier={() => {
								didEditAnythingRef.current = true;
							}}
							onSomeDropdownOpenChange={setIsSomeDropdownOpen}
							onCancel={handleRequestClose}
							onSave={handleSave}
						/>
					</LocationCellProvider>
				}
			>
				{children}
			</SystemReactTinyPopover>
			<SystemDialog
				open={displayCloseWarning}
				onClose={() => setDisplayCloseWarning(false)}
				title={t("schedule.template.v3.editor.discard.title")}
				body={t("schedule.template.v3.editor.discard.body")}
				onConfirm={close}
				cancel={t("schedule.template.v3.editor.discard.cancel")}
				confirm={t("schedule.template.v3.editor.discard.confirm")}
				dataTestId="ms3-create-discard"
			/>
		</>
	);
};
